import { useEffect, useState } from 'react'

import { WorkflowTemplate } from '@digital-twins/shared-types'

import { workflowsApi } from '@/lib/api'

/**
 * Fetches ONE workflow template by its template token (Step 1c-ii,
 * thread-output-continuity — closes D1).
 *
 * `composeRunStudyDoc(run, runTemplate)` needs a template to resolve the
 * run's audience input key, and `useRunResults(runToken)` hands back a run
 * whose `templateToken` nothing else on the page can turn into a template.
 * `useWorkflowRun` (`@/lib/workflows/useWorkflowRun.ts`, :322-345) fetches a
 * template too, but only for the thread's CURRENT run — it is thread-scoped
 * on both halves. This hook is the run-scoped counterpart: given the
 * `templateToken` off any run (past or current), it reads that template and
 * nothing else. Read-only; no gate/retry/cancel actions.
 */

/** Why the template could not be read. Same two branches, same shape and same
 *  402 detection as `RunResultsError` in `useRunResults.ts` (kept local for
 *  the same reason: a consumer rendering a past run reads one error shape
 *  whichever of the two fetches failed). */
export interface RunTemplateError {
  kind: 'payment_required' | 'request_failed'
  message: string
}

export interface UseRunTemplateResult {
  template: WorkflowTemplate | null
  isLoading: boolean
  error: RunTemplateError | null
}

const PAYMENT_REQUIRED_MESSAGE = 'Workflow runs need an active subscription.'
const REQUEST_FAILED_MESSAGE = 'The template for this run could not be loaded.'

function readHttpStatus(cause: unknown): number | null {
  if (typeof cause !== 'object' || cause === null) {
    return null
  }
  const response = (cause as { response?: { status?: unknown } }).response
  if (!response || typeof response.status !== 'number') {
    return null
  }
  return response.status
}

function toRunTemplateError(cause: unknown): RunTemplateError {
  if (readHttpStatus(cause) === 402) {
    return { kind: 'payment_required', message: PAYMENT_REQUIRED_MESSAGE }
  }
  return { kind: 'request_failed', message: REQUEST_FAILED_MESSAGE }
}

/**
 * Module-level, keyed by template token, shared across every
 * `useRunTemplate` instance on the page.
 *
 * Caching is genuinely safe here in a way it is not for runs: a template is
 * an immutable, versioned registry record addressed by token (a new version
 * is a new token — `workflowsApi.getTemplate` is `@IsUUID()`-keyed and never
 * takes a slug), whereas a run mutates through its whole lifecycle. So a
 * template read once in a session cannot go stale for the rest of it, and a
 * thread whose several announced runs all came from the same template pays
 * for exactly one request no matter how many announcement turns mount.
 *
 * Storing the PROMISE (not the resolved value) is what lets two mounts that
 * fire before the first response lands share it too: the second gets the same
 * in-flight promise rather than starting a second request.
 *
 * A failed fetch is evicted so the next mount gets a fresh attempt instead of
 * being stuck on a cached rejection forever.
 */
const runTemplateCache = new Map<string, Promise<WorkflowTemplate>>()

function fetchTemplateCached(templateToken: string): Promise<WorkflowTemplate> {
  const cached = runTemplateCache.get(templateToken)
  if (cached) {
    return cached
  }
  const promise = workflowsApi.getTemplate(templateToken).catch((cause: unknown) => {
    runTemplateCache.delete(templateToken)
    throw cause
  })
  runTemplateCache.set(templateToken, promise)
  return promise
}

/**
 * Reads one workflow template by token, lazily.
 *
 * Fetches ONLY when `templateToken` is non-null — a `null` token (a run that
 * has not loaded yet, or a turn that is not a run announcement at all) must
 * never trigger a request on mount.
 *
 * On a token change the previous template is CLEARED before the new one
 * loads. `useRunResults` leaves its previous run in state while the next
 * token resolves, so a consumer briefly renders run A's data under token B;
 * that is a bug not worth copying, because a stale template silently resolves
 * the WRONG audience input key in `composeRunStudyDoc` and the resulting
 * StudyDoc looks plausible rather than empty.
 */
export function useRunTemplate(templateToken: string | null): UseRunTemplateResult {
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<RunTemplateError | null>(null)

  useEffect(() => {
    if (!templateToken) {
      setTemplate(null)
      setIsLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setTemplate(null)
    setIsLoading(true)
    setError(null)

    fetchTemplateCached(templateToken)
      .then((next) => {
        if (cancelled) {
          return
        }
        setTemplate(next)
        setIsLoading(false)
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return
        }
        setIsLoading(false)
        setError(toRunTemplateError(cause))
      })

    return () => {
      cancelled = true
    }
  }, [templateToken])

  return { template, isLoading, error }
}
